import React, { useState, useEffect } from "react";
import { Search, Send, CheckCheck } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {API_URL} from './config';
const Chart = () => {
  const [chartsData, setChartsData] = useState([]);
  const [selectedChart, setSelectedChart] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const pic = "https://srv547457.hstgr.cloud:3020";

  useEffect(() => {
    const fetchCharts = async () => {
      try {
        const response = await fetch(`${API_URL}/customer_admin_chats/admin-charts`);
        const data = await response.json();
        setChartsData(data);
      } catch (error) {
        console.error("Error fetching charts:", error);
      }
    };

    fetchCharts();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (selectedChart) {
        fetchChatMessages(selectedChart.userid);
      }
    }, 1000); // Poll every 5 seconds

    return () => clearInterval(intervalId); // Clear the interval on component unmount
  }, [selectedChart]);

  const fetchChatMessages = async (userid) => {
    setFetchError(null);
    try {
      const response = await fetch(`${API_URL}/customer_admin_chats/${userid}`);

      if (!response.ok) {
        setFetchError("Failed to fetch messages.");
        return;
      }

      const result = await response.json();
      if (result && (result.status === 200 || result.status === "200") && Array.isArray(result.data)) {
        const newMessages = result.data;

        // Update chat history only if there are new messages
        setChatHistory((prevHistory) => {
          const existingIds = prevHistory.map(msg => msg.customer_admin_chat_id);
          const filteredNewMessages = newMessages.filter(msg => !existingIds.includes(msg.customer_admin_chat_id));

          if (filteredNewMessages.length > 0) {
            return [...prevHistory, ...filteredNewMessages].sort((a, b) => 
              a.customer_admin_chat_id - b.customer_admin_chat_id
            );
          }
          return prevHistory;
        });
      } else {
        setFetchError("No messages available.");
      }
    } catch (error) {
      setFetchError("Error fetching messages.");
    }
  };

  const handleChartSelect = (chart) => {
    setSelectedChart(chart);
    fetchChatMessages(chart.userid);
  };

  const filteredCharts = chartsData.filter((chart) =>
    `${chart.user_name} ${chart.user_surname}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSend = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior

    if (newMessage.trim()) {
      const now = new Date();
      const dateChat = now.toISOString().split("T")[0]; // Current date
      const timeChat = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }); // Current time with seconds

      const messageToSend = {
        date_chat: dateChat,
        time_chat: timeChat,
        trip_id: selectedChart.trip_id,
        admin_id: 1, // Replace with the actual admin ID if applicable
        driver_id: selectedChart.userid,
        conversation_id: selectedChart.conversation_id,
        message: newMessage.trim(),
        origin: 1, // Use customerId as origin for sent messages
      };

      try {
        const response = await fetch(`${API_URL}/customer_admin_chats/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(messageToSend),
        });

        const result = await response.json();
        console.log("Message sent:", result);

        // Append the new message to the chat history
        setChatHistory(prevHistory => {
          const updatedHistory = [...prevHistory, messageToSend].sort((a, b) => {
            const dateA = new Date(`${a.date_chat} ${a.time_chat}`);
            const dateB = new Date(`${b.date_chat} ${b.time_chat}`);
            return dateA - dateB; // Sort in ascending order
          });
          return updatedHistory; // Return the updated sorted chat history
        });

        setNewMessage(""); // Clear input
      } catch (error) {
        console.error("Error posting message:", error);
      }
    }
  };

  const Message = ({ message }) => {
    const isSender = message.origin == 1; // Adjust this condition based on your logic

    return (
      <motion.div
        key={message.customer_admin_chat_id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex ${isSender ? "justify-end" : "justify-start"}`}
      >
        <div
          className={`max-w-xl rounded-lg px-4 py-2 ${
            isSender ? "bg-[#ffc000] text-white" : "bg-white border border-gray-200"
          }`}
        >
          <p className="text-sm">{message.message}</p>
          <div className="flex items-center justify-end mt-1 space-x-1">
            <span className="text-xs text-gray-500">{formatTime(message.date_chat)}</span>
            <CheckCheck size={14} className="text-gray-500" />
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Messages</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search conversations..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#ffc000]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          <AnimatePresence>
            {filteredCharts.map((chart) => (
              <motion.div
                key={chart.userid}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors duration-150 ${selectedChart?.userid === chart.userid ? "bg-[#fff8e6]" : ""}`}
                onClick={() => handleChartSelect(chart)}
              >
                <div className="flex items-center">
                  <div className="relative">
                    <img
                      src={`${pic}/${chart.profile}`}
                      alt={`${chart.user_name} profile`}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <h className="font-semibold text-gray-800">{`${chart.user_name} ${chart.user_surname}`}</h>
                      <p className="text-xs text-gray-500">{chart.last_message_date}</p>
                      <p className="text-xs text-gray-500">{chart.last_message_time}</p>
                    </div>
                    <p className="text-sm text-gray-500 truncate">{chart.last_message}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col bg-gray-50">
        {selectedChart ? (
          <>
            {/* Chat Header */}
            <div className="h-16 border-b border-gray-200 px-6 flex items-center bg-white">
              <img
                src={`${pic}/${selectedChart.profile}`}
                alt={`${selectedChart.user_name} profile`}
                className="w-10 h-10 rounded-full"
              />
              <div className="ml-4">
                <h3 className="font-semibold text-gray-800">{`${selectedChart.user_name} ${selectedChart.user_surname}`}</h3>
                <span className="text-sm text-green-500">Online</span>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6">
              {fetchError ? (
                <div className="text-center text-red-500 mt-4">{fetchError}</div>
              ) : (
                <div className="space-y-4">
                  {chatHistory.map((message) => (
                    <Message key={message.customer_admin_chat_id} message={message} />
                  ))}
                </div>
              )}
            </div>

            {/* Message Input */}
            <form onSubmit={handleSend} className="p-4 bg-white border-t border-gray-200">
              <div className="flex items-center space-x-4">
                <input
                  type="text"
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-[#ffc000]"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                />
                <button type="submit" className="px-4 py-2 bg-[#ffc000] text-white rounded-lg hover:bg-[#e6ac00] transition-colors duration-150">
                  <Send size={20} />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#ffc000] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCheck size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800">Select a conversation</h3>
              <p className="text-gray-500 mt-2">Choose from your existing conversations</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chart;